import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { machineService } from "@/services/machines";
import { setMachineStatusUpdater } from "@/services/socketService";
import { toInternalStatus } from "@/utils/machineStatus";
import type { Machine } from "@/types/machines";
import {
  useCompanyFilterStore,
  selectSelectedCompany,
} from "@/stores/companyFilterStore";
import {
  useAuthStore,
  selectIsAdmin,
  selectUserIoTCompany,
} from "@/stores/authStore";

export const machinesKeys = {
  all: ["machines"] as const,
  list: (filters?: any) => [...machinesKeys.all, "list", filters ?? {}] as const,
  detail: (id: string) => [...machinesKeys.all, "detail", id] as const,
  realtime: (id: string) =>
    [...machinesKeys.all, "realtime", id] as const,
  external: () => [...machinesKeys.all, "external"] as const,
};

/**
 * Grava o status vivo do socket DIRETO no cache das listas de máquinas
 * (setQueriesData — atualização de dado, não invalidação: não dispara refetch
 * nem o flicker de "—" que motivou a regra de nunca invalidar por evento).
 *
 * Sem isso o status vivo morava só em useState dos componentes: qualquer troca
 * de tela remontava o card, que re-semeava do cache — e o cache ainda dizia
 * "offline" da carga inicial. Com o cache atualizado, remount nasce certo.
 *
 * O evento do socket carrega o SERIAL; as listas indexam por id (Device.name).
 * Onde name ≠ serial (formato de produção), casar pelos dois é o que faz
 * contadores e filtros — que leem o cache — se corrigirem também.
 */
export function registrarStatusAoVivoNoCache(queryClient: QueryClient) {
  setMachineStatusUpdater((serial, statusValue) => {
    const status = toInternalStatus(statusValue);
    queryClient.setQueriesData<any>(
      { queryKey: [...machinesKeys.all, "list"] },
      (prev: any) => {
        if (!prev) return prev;
        const list = Array.isArray(prev) ? prev : prev.data;
        if (!Array.isArray(list)) return prev;
        let changed = false;
        const next = list.map((m: any) => {
          if (m.serial !== serial && m.id !== serial) return m;
          if (m.status === status) return m;
          changed = true;
          return { ...m, status };
        });
        if (!changed) return prev;
        return Array.isArray(prev) ? next : { ...prev, data: next };
      },
    );
  });
}

/**
 * Fetch the full machines list, scoped to the user's active company.
 *  - Admins: scope follows the company picker (empty string → all companies).
 *  - Non-admins: always scoped to their own iotCompany.
 *
 * The query key includes the active company so switching companies refetches
 * automatically instead of returning stale cached data.
 */
export function useMachinesQuery(options?: {
  enabled?: boolean;
  staleTime?: number;
}) {
  const isAdmin = useAuthStore(selectIsAdmin);
  const userCompany = useAuthStore(selectUserIoTCompany);
  const selectedCompany = useCompanyFilterStore(selectSelectedCompany);
  const activeCompany = isAdmin ? selectedCompany : userCompany;
  const queryClient = useQueryClient();
  const queryKey = machinesKeys.list({ company: activeCompany || "__all__" });

  return useQuery({
    queryKey,
    queryFn: () => {
      return machineService
        .getAll(
          activeCompany || undefined,
          undefined,
          // `availability` is computed in background after the initial machine
          // list resolves. Pushing each value into the React Query cache forces
          // a re-render so the Dashboard's "Disponibilidade Geral" card and
          // per-machine bars fill in progressively instead of staying at 0%.
          (serial, value) => {
            queryClient.setQueryData<any>(queryKey, (prev: any) => {
              if (!prev) return prev;
              const list = Array.isArray(prev) ? prev : prev.data;
              if (!Array.isArray(list)) return prev;
              let changed = false;
              const next = list.map((m: any) => {
                if ((m.serial ?? m.id) !== serial) return m;
                if (m.availability === value) return m;
                changed = true;
                return { ...m, availability: value };
              });
              if (!changed) return prev;
              return Array.isArray(prev) ? next : { ...prev, data: next };
            });
          },
          // Fires once when EVERY machine's availability fetch has finished
          // (success or error). The Dashboard listens for this to hide the
          // "calculando…" badge — unlike `dataUpdatedAt`, this signal isn't
          // bumped by unrelated WebSocket invalidations.
          () => {
            window.dispatchEvent(new CustomEvent("machinesAvailabilityComplete"));
          },
        )
        .then((result: any) => {
          // Preserve availability already computed for this company so a refetch
          // never flashes the cards back to "—". We read the LIVE cache here (at
          // resolution time) instead of snapshotting at the start of queryFn:
          // the background worker keeps writing availability into the cache via
          // setQueryData *during* the fetch, and reading it now captures those
          // fills too — closing the race that made values appear and then revert
          // to "—" (notably right after switching the company filter).
          const liveData = queryClient.getQueryData<any>(queryKey);
          const liveList = Array.isArray(liveData) ? liveData : liveData?.data;
          if (!Array.isArray(liveList) || liveList.length === 0) return result;

          // Previously computed availability (including 0 = legitimately offline)
          // keyed by serial/id. Machines still null (never computed) are skipped.
          const prevAvail = new Map<string, number>();
          for (const m of liveList) {
            const key = m.serial ?? m.id;
            if (key && typeof m.availability === "number") {
              prevAvail.set(key, m.availability);
            }
          }
          if (prevAvail.size === 0) return result;

          const list = Array.isArray(result) ? result : result?.data;
          if (!Array.isArray(list)) return result;
          let changed = false;
          const next = list.map((m: any) => {
            const key = m.serial ?? m.id;
            // Restore the prior value only while this machine's freshly fetched
            // availability is still null (the new worker hasn't refilled it yet).
            if (key && m.availability == null && prevAvail.has(key)) {
              changed = true;
              return { ...m, availability: prevAvail.get(key) };
            }
            return m;
          });
          if (!changed) return result;
          return Array.isArray(result) ? next : { ...result, data: next };
        });
    },
    enabled: options?.enabled ?? true,
    staleTime: options?.staleTime ?? 30_000,
  });
}

export function useMachineQuery(id: string) {
  return useQuery({
    queryKey: machinesKeys.detail(id),
    queryFn: () => machineService.getById(id),
    enabled: !!id,
    // Match httpClient's 5 min cache TTL so navigating back/forward within
    // a few minutes doesn't trigger a refetch (data isn't stale that fast).
    staleTime: 5 * 60_000,
  });
}

export function useExternalMachinesQuery() {
  return useQuery({
    queryKey: machinesKeys.external(),
    queryFn: () => machineService.getAllFromExternalAPI(),
    staleTime: 30_000,
  });
}

/**
 * Invalidate all machines queries. Call this after a WebSocket update,
 * mutation, or login/logout.
 */
export function useInvalidateMachines() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: machinesKeys.all });
}

export function useCreateMachineMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Machine, "id" | "lastUpdate">) =>
      machineService.create(data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: machinesKeys.all }),
  });
}

export function useUpdateMachineMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Machine> }) =>
      machineService.update(id, updates),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: machinesKeys.all }),
  });
}

export function useDeleteMachineMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => machineService.delete(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: machinesKeys.all }),
  });
}
